"use client";

import type { FormField } from "@/lib/types/form-fields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Disabled, presentational preview of the registration form attendees see.
 * Used inside the editor's live preview pane and on the public page.
 */
export function RegistrationFormPreview({
  fields,
  collectPhone,
}: {
  fields: FormField[];
  /** Convenience: render the phone field even if not in the array. */
  collectPhone?: boolean;
}) {
  // De-dupe phone if both flag and field exist
  const seen = new Set<string>();
  const visible: FormField[] = [];
  if (collectPhone && !fields.some((f) => f.type === "phone")) {
    visible.push({
      id: "_phone",
      type: "phone",
      label: "Phone",
      required: false,
    });
  }
  for (const f of fields) {
    const key = f.type === "phone" ? "phone" : f.id;
    if (seen.has(key)) continue;
    seen.add(key);
    visible.push(f);
  }

  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="font-serif text-xl">Reserve your place</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Fill in your details to register. (Preview — disabled.)
      </p>

      <fieldset disabled className="mt-6 space-y-5 opacity-95">
        <FieldRow label="Name" required>
          <Input placeholder="Your name" />
        </FieldRow>
        <FieldRow label="Email" required>
          <Input type="email" placeholder="you@example.com" />
        </FieldRow>

        {visible.map((f) => (
          <FieldRender key={f.id} field={f} />
        ))}

        <button
          type="button"
          className={cn(
            buttonVariants({ size: "lg" }),
            "w-full rounded-full"
          )}
        >
          Confirm registration
        </button>
      </fieldset>
    </div>
  );
}

function FieldRender({ field }: { field: FormField }) {
  switch (field.type) {
    case "phone":
      return (
        <FieldRow label={field.label} required={field.required}>
          <Input type="tel" placeholder="+27 …" />
        </FieldRow>
      );
    case "long_text":
      return (
        <FieldRow label={field.label} required={field.required}>
          <Textarea rows={3} placeholder={field.placeholder ?? ""} />
        </FieldRow>
      );
    case "select":
      return (
        <FieldRow label={field.label} required={field.required}>
          <select className="h-9 w-full rounded-lg border bg-background px-3 text-sm">
            <option>Choose…</option>
            {field.options.map((o) => (
              <option key={o.value}>{o.label}</option>
            ))}
          </select>
        </FieldRow>
      );
    case "multiselect":
      return (
        <FieldRow label={field.label} required={field.required}>
          <div className="space-y-2">
            {field.options.map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox /> {o.label}
              </label>
            ))}
          </div>
        </FieldRow>
      );
    case "number":
      return (
        <FieldRow label={field.label} required={field.required}>
          <Input
            type="number"
            min={field.min ?? undefined}
            max={field.max ?? undefined}
          />
        </FieldRow>
      );
    case "file":
      return (
        <FieldRow
          label={field.label}
          required={field.required}
          hint={`Up to ${field.max_size_mb ?? 5}MB`}
        >
          <Input type="file" />
        </FieldRow>
      );
    case "consent":
      return (
        <label className="flex items-start gap-3 text-sm">
          <Checkbox className="mt-0.5" />
          <span className="text-muted-foreground">
            {field.label}
            {field.required && <span className="text-destructive"> *</span>}
          </span>
        </label>
      );
    case "divider":
      return (
        <div className="border-t pt-4">
          {field.label && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {field.label}
            </p>
          )}
        </div>
      );
  }
}

function FieldRow({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
